import {acceptHMRUpdate, defineStore} from 'pinia';
import {computed, ref} from 'vue';
import useApi from '~/composables/useApi';
import type {RieCommand, RieDetailResource, SectorResource} from '~/lib/types/rieTypes';
import type {ProblemDetail} from '~/lib/types/fetchErrors';
import {useCompanyContextStore} from '~/store/company/companyContext';
import {parseProblemDetail} from '~/middleware/api';
import {rieCommandFormLabels} from '~/composables/useRieForm';

export const useCompanyRiesStore = defineStore('companyRies', () => {
	const {GET, POST, PUT, DELETE} = useApi();

	const loading = ref(false);
	const sectorsLoading = ref(false);
	const rie = ref<RieDetailResource>();
	const ries = ref<RieDetailResource[]>([]);
	const sectors = ref<SectorResource[]>([]);
	const problemDetail = ref<ProblemDetail>();
	const currentRieId = ref<number>();
	const currentCompanyId = ref<number>();

	const companyContextStore = useCompanyContextStore();

	/**
	 * Update the data in companyContextStore when something about the ries changes to prevent stale data in the UI
	 * @param companyId
	 */
	const updateCompanyContext = (companyId: number): void => {
		companyContextStore.fetchCompanyContext(companyId).finally(() => {
			// No need to do anything with the result here
		});
	};

	const updateRies = async (data: RieDetailResource) => {
		const rieIndex = ries.value.findIndex((rie) => rie.id === data.id);
		if (rieIndex === -1) {
			ries.value.push(data);
		} else {
			ries.value[rieIndex] = data;
		}
	};

	const getRie = computed(() => (rieId: number) => {
		return ries.value.find((rie) => rie.id === rieId);
	});

	const fetchRie = async (rieId: number) => {
		if (isNaN(rieId) || rieId <= 0) {
			throw new TypeError('The provided RI&E ID is invalid!');
		}

		loading.value = true;
		problemDetail.value = undefined;

		const {data, error} = await GET(`/rie/{rieId}`, {
			params: {
				path: {
					rieId: rieId.toString()
				}
			}
		});

		loading.value = false;

		if (error || !data) {
			loading.value = false;
			problemDetail.value = error;
			return false;
		}

		rie.value = data;
		await updateRies(data);

		return true;
	};

	const fetchSectors = async () => {
		sectorsLoading.value = true;
		problemDetail.value = undefined;
		sectors.value = [];

		const {data, error} = await GET(`/sectors`);

		if (error || !data) {
			sectorsLoading.value = false;
			problemDetail.value = error;
			return;
		}

		sectors.value = data;
		sectorsLoading.value = false;
		return true;
	};

	const fetchCompanyRies = async (companyId: number) => {
		loading.value = true;
		problemDetail.value = undefined;
		if (companyId !== currentCompanyId.value) {
			ries.value = [];
		}
		currentCompanyId.value = companyId;

		const {data, error} = await GET(`/rie`, {
			params: {
				query: {
					companyId: companyId.toString()
				}
			}
		});

		if (error || !data) {
			loading.value = false;
			problemDetail.value = error;
			return false;
		}

		ries.value = data;
		loading.value = false;
		return true;
	};

	const submitRie = async (companyId: number, command: RieCommand) => {
		loading.value = true;
		problemDetail.value = undefined;

		const {data, error} = await POST('/rie', {
			params: {
				query: {
					companyId: companyId.toString()
				}
			},
			body: command
		});

		if (error || !data) {
			loading.value = false;
			problemDetail.value = parseProblemDetail(error, rieCommandFormLabels);
			return false;
		}

		rie.value = data;
		await updateRies(data);

		loading.value = false;

		updateCompanyContext(companyId);

		return true;
	};

	const updateRie = async (companyId: number, rieId: number, command: RieCommand) => {
		loading.value = true;
		problemDetail.value = undefined;

		const {data, error} = await PUT('/rie/{rieId}', {
			params: {
				path: {
					rieId: rieId.toString()
				}
			},
			body: command
		});

		if (error || !data) {
			loading.value = false;
			problemDetail.value = parseProblemDetail(error, rieCommandFormLabels);
			return false;
		}

		await updateRies(data);

		loading.value = false;

		updateCompanyContext(companyId);

		return true;
	};

	const deleteRie = async (companyId: number, rieId: number) => {
		loading.value = true;
		problemDetail.value = undefined;

		const {error} = await DELETE('/rie/{rieId}', {
			params: {
				path: {
					rieId: rieId.toString()
				}
			}
		});

		if (error) {
			loading.value = false;
			problemDetail.value = error;
			return false;
		}

		if (currentRieId.value === rieId) {
			currentRieId.value = undefined;
		}

		ries.value = ries.value.filter((rie) => rie.id !== rieId);

		loading.value = false;

		updateCompanyContext(companyId);

		return true;
	};

	return {
		updateRie,
		updateRies,
		getRie,
		fetchRie,
		fetchSectors,
		fetchCompanyRies,
		submitRie,
		loading,
		sectorsLoading,
		problemDetail,
		rie,
		ries,
		sectors,
		deleteRie
	};
});

if (import.meta.hot) {
	import.meta.hot.accept(acceptHMRUpdate(useCompanyRiesStore, import.meta.hot));
}
